import React, { useEffect, useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import ValidatePlanningDialog from '../tools/ValidatePlanningDialog';
import ValidationPlanningService from "../../services/ValidationPlanningService";
import TraceabilityService from "../../services/TraceabilityService";
import useId from '@mui/material/utils/useId';

// Fonction utilitaire pour obtenir la taille de la fenêtre
const getWindowSize = () => {
    return { 
        innerWidth: window.innerWidth, 
        innerHeight: window.innerHeight 
    };
};

export default function TableView(props) {
    // Destructuration des props
    const {
        start_time: startTime,
        end_time: endTime,
        difference_In_Days,
        groups,
        items,
        id_parameter,
        openValidatePlanningTimeline,
        handleRefusedTimeline,
        closeValidate,
    } = props;

    // États du composant
    const [openValidatePlanning, setOpenValidatePlanning] = useState(false);
    const [idPlanning, setIdPlanning] = useState(0);
    const [userId, setUserId] = useState(0);
    const [tableData, setTableData] = useState([]);

    // Mémoïsation des en-têtes de dates pour éviter des recalculs inutiles
    const dateHeaders = useMemo(() => {
        const headers = [];
        const startDate = moment(startTime);
        
        for (let i = 0; i <= difference_In_Days; i++) {
            const currentDate = moment(startDate).add(i, 'days');
            headers.push({
                date: currentDate.format('YYYY-MM-DD'),
                day: currentDate.format('DD'),
                weekday: currentDate.format('ddd'),
                month: currentDate.format('MMMM') // Ajouter le mois
            });
        }
        
        return headers;
    }, [startTime, difference_In_Days]);

    // Fonction pour déterminer la couleur en fonction de l'état
    const getItemColor = useCallback((item) => {
        switch (item.itemProps) {
            case 1: return '#008000'; // confirmed
            case 0: return '#FF0000'; // declined
            case 2: return '#65C5EB'; // pending
            case 3: return '#133E67'; // autre pending
            default: return '#6ACFF6';
        }
    }, []);

    // Conversion des données en format tableau - optimisé avec useMemo
    useEffect(() => {
        const processItems = () => {
            return groups.map(group => {
                //const groupItems = items.filter(item => item.group === group.id);
                const groupItems = items.filter(item => item.user_id === group.userId);
                
                const row = { id: group.id, title: group.title,useId:group.userId, days: {} };

                // Initialisation des jours
                dateHeaders.forEach(header => { 
                    row.days[header.date] = null;
                });
                 
                
                // Remplissage des éléments
                groupItems.forEach(item => {
                    const itemStart = moment(item.start_time);
                    const itemEnd = moment(item.end_time);
                    let currentDate = moment(itemStart);
                    
                    const color = getItemColor(item);
                    
                    while (currentDate.isSameOrBefore(itemEnd)) {
                        const dateStr = currentDate.format('YYYY-MM-DD');
                        
                        row.days[dateStr] = {
                            id: item.planning_id || item.id,
                            userId: item.user_id,
                            color,
                            isStart: currentDate.isSame(itemStart, 'day'),
                            isEnd: currentDate.isSame(itemEnd, 'day')
                        };
                        
                        currentDate.add(1, 'day');
                    }
                });

                return row;
            });
        };
        
        setTableData(processItems());
    }, [items, groups, dateHeaders, getItemColor]);

    // Synchronisation de l'état de la boîte de dialogue
    useEffect(() => {
        setOpenValidatePlanning(openValidatePlanningTimeline);
    }, [openValidatePlanningTimeline]);

    // Gestionnaire de clic sur une cellule
    const handleCellClick = useCallback((id, userId) => {
        setOpenValidatePlanning(true);
        setIdPlanning(id);
        setUserId(userId);
    }, []);

    // Fermeture de la boîte de dialogue
    const handleCloseDialog = useCallback(() => {
        setOpenValidatePlanning(false);
    }, []);

    // Validation de la timeline
    const handleValidateTimeline = useCallback(() => {
        const validation_state = 0;
        const observation = null;
        
        ValidationPlanningService.confirmedVP(idPlanning).then((res) => {
            TraceabilityService.updateTVP(
                observation,
                validation_state,
                res.data
            ).then((result) => {
                ValidationPlanningService.verification(userId, id_parameter).then((r) => {
                    if (r?.status) {
                        closeValidate();
                        setOpenValidatePlanning(false);
                    }
                });
            });
        });
    }, [idPlanning, userId, id_parameter, closeValidate]);

    // Rendu du tableau
    return (
        <div>
            <ValidatePlanningDialog 
                openDialog={openValidatePlanning} 
                closeDialog={handleCloseDialog}
                idPlanning={idPlanning} 
                handleRefused={handleRefusedTimeline} 
                handleValidate={handleValidateTimeline} 
            />

            <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontFamily: 'Arial, sans-serif'
                }}>
                    <thead>
                        <tr>
                            <th style={{ 
                                position: 'sticky', 
                                left: 0, 
                                backgroundColor: '#f8f9fa',
                                minWidth: '150px',
                                textAlign: 'left',
                                padding: '8px',
                                border: '1px solid #ddd'
                            }}>Prénom(s)</th>
                            {dateHeaders.map((header, index) => (
                                <th key={header.date} style={{ 
                                    minWidth: '50px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    textAlign: 'center',
                                    backgroundColor: index === 0 ? '#f8f9fa' : 'transparent'
                                }}>
                                    <div>{header.weekday}</div>
                                    <div>{header.day}</div>
                                    <div>{header.month}</div>
                                    {/* <div style={{
                                            fontSize: '0.8em',
                                            marginTop: '5px',
                                            fontWeight: 'bold'
                                    }}>
                                            {header.month}
                                        </div>
                                  */}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map(row => (
                            <tr key={row.id}>
                                <td style={{ 
                                    position: 'sticky', 
                                    left: 0, 
                                    backgroundColor: '#f8f9fa',
                                    padding: '8px',
                                    border: '1px solid #ddd'
                                }}>{row.title}</td>
                                {dateHeaders.map(header => {
                                    const cell = row.days[header.date];
                                    if (!cell) {
                                        return <td 
                                            key={`${row.id}-${header.date}`} 
                                            style={{ border: '1px solid #ddd', height: '40px' }}
                                        />;
                                    }
                                    
                                    return (
                                        <td 
                                            key={`${row.id}-${header.date}`}
                                            onClick={() => handleCellClick(cell.id, cell.userId)}
                                            style={{ 
                                                backgroundColor: cell.color,
                                                border: '1px solid #ddd',
                                                height: '40px',
                                                cursor: 'pointer',
                                                position: 'relative'
                                            }}
                                        >
                                            {cell.isStart && (
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '2px',
                                                    top: '50%',
                                                    width: '4px',
                                                    height: '20px',
                                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                                    transform: 'translateY(-50%)'
                                                }} />
                                            )}
                                            {cell.isEnd && (
                                                <div style={{
                                                    position: 'absolute',
                                                    right: '2px',
                                                    top: '50%',
                                                    width: '4px',
                                                    height: '20px',
                                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                                    transform: 'translateY(-50%)'
                                                }} />
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
